import React, { useContext, useEffect, useRef } from 'react';
import { func, string, object } from 'prop-types';
import { IconButton } from '@nike/nike-design-system-components';
import { Menu } from '@nike/nike-design-system-icons';
import { AnalyticsContext } from '../../context/analyticsContext';
import createBtnLinkKeyDownHandler from './../../utils/createBtnLinkKeyDownHandler';
import useBodega from '../../utils/hooks/bodegaHook';
import { BODEGA_DEFAULTS } from '../../constants';
import templateString from '../../utils/templateString';

/**
 * Button to open the mobile nav drawer
 * @param {object} props
 * @param {string} props.className
 * @param {Function} props.setMobileNavDrawer
 * @param {object} props.shouldFocusMobileMenuBtn
 * @returns {React.ReactElement}
 */
const MobileNavBtn = ({ className, setMobileNavDrawer, shouldFocusMobileMenuBtn }) => {
  const { analyticsClient } = useContext(AnalyticsContext);
  const ariaLabelForIcon = templateString(useBodega('nav_menu_aria_label', BODEGA_DEFAULTS.MENU), {
    menu: '',
  }).trim();

  const handleClick = () => {
    if (setMobileNavDrawer) {
      setMobileNavDrawer(currentValue => !currentValue);
    }
    // Send the analytics event with data.
    analyticsClient?.clickOpenMobileMenu();
  };

  const keyDownHandler = event => {
    switch (event.which) {
      case 32: // SPACE
        handleClick();
        break;

      default:
        break;
    }
  };

  const handleKeyDown = createBtnLinkKeyDownHandler(keyDownHandler);

  const mobileNavBtnRef = useRef(false);
  /**
   * When we close the mobile menu we want focus to be returned to this button
   * unfortunately during the rendering that focus is being lost and set on the body.
   * shouldFocusMobileMenuBtn is being set to true by the mobile menu when it closes
   * every render we check if we should focus this button
   */
  useEffect(() => {
    if (shouldFocusMobileMenuBtn.current) {
      mobileNavBtnRef.current.focus();
      shouldFocusMobileMenuBtn.current = false;
    }
  });

  return (
    <IconButton
      Component="button"
      Icon={<Menu />}
      aria-haspopup="true"
      ariaLabel={ariaLabelForIcon}
      background="dark"
      className={className}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      ref={mobileNavBtnRef}
      size="small"
      type="button"
    />
  );
};

export default MobileNavBtn;

MobileNavBtn.propTypes = {
  className: string,
  setMobileNavDrawer: func,
  shouldFocusMobileMenuBtn: object,
};
